import React, { useState } from 'react';
import { CustomRegistrationQuestion, RegistrationQuestion } from '../types/RegistrationQuestion';
import { CreateEvent } from '../types/Event';
import { v4 as uuidv4 } from 'uuid';
import { MEDIA_FILE_IDS } from '../constants/media-files';

interface RegistrationQuestionsPageProps {
  eventDetails: CreateEvent;
  registrationQuestions: RegistrationQuestion[];
  eventType: string;
  handleQuestionOptionChange: (questionId: string, option: 'Required' | 'Optional' | 'Omit') => void;
  customRegistrationQuestions: CustomRegistrationQuestion[];
  setCustomRegistrationQuestions: React.Dispatch<React.SetStateAction<CustomRegistrationQuestion[]>>;
}

export const RegistrationQuestionsPage: React.FC<RegistrationQuestionsPageProps> = ({
  eventDetails,
  registrationQuestions,
  eventType,
  handleQuestionOptionChange,
  customRegistrationQuestions,
  setCustomRegistrationQuestions
}) => {
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [newQuestionText, setNewQuestionText] = useState('');

  // 🆕 State for expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const handleToggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleRemoveCustomQuestion = (id: string) => {
    setCustomRegistrationQuestions(prev => prev.filter(q => q.custom_registration_question_id !== id));
  };

  const filteredQuestions = registrationQuestions.filter((question) => {
    const isMedia = question.preset_registration_question_id in MEDIA_FILE_IDS;
    if (isMedia && !eventDetails.has_media) {
      return false;
    }
    if (eventType === 'MUSIC') {
      return question.is_music;
    } else if (eventType === 'SPORTS') {
      return question.is_sports;
    } else {
      return question.is_general;
    }
  });

  const groupedByCategory: Record<string, RegistrationQuestion[]> = filteredQuestions.reduce((groups, question) => {
    const category = question.category || 'Uncategorized';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(question);
    return groups;
  }, {} as Record<string, RegistrationQuestion[]>);

  for (const category in groupedByCategory) {
    groupedByCategory[category].sort((a, b) => a.display_order - b.display_order);
  }

  const handleInfoClick = (questionId: string) => {
    setSelectedQuestionId(prevId => (prevId === questionId ? null : questionId));
  };

  const handleAddCustomQuestion = () => {
    if (customRegistrationQuestions.length >= 10) return;
    if (!newQuestionText.trim()) return;
    setCustomRegistrationQuestions([
      ...customRegistrationQuestions,
      {
        event_id: '',
        custom_registration_question_id: uuidv4(),
        custom_question_text: newQuestionText.trim(),
        is_required: false
      }
    ]);
    setNewQuestionText('');
  };

  const handleCustomRequiredChange = (id: string, isRequired: boolean) => {
    setCustomRegistrationQuestions(prevQuestions =>
      prevQuestions.map(q =>
        q.custom_registration_question_id === id
          ? { ...q, is_required: isRequired }
          : q
      )
    );
  };

  return (
    <div className='card' style={{ maxWidth: '750px'}}>
      <h1>Registration Questions</h1>
      <p>Add questions to your event registration form to collect important information from registrants. This data can help with event planning, communication, and participant management.</p>
      <h2>* Name, Email, and Phone Number will be collected by default.</h2>

{/* Custom Questions Section */}
      <div style={{ marginTop: '20px' }}>
        <h2>Custom Questions ({10 - customRegistrationQuestions.length} Remaining)</h2>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="text"
            value={newQuestionText}
            onChange={(e) => setNewQuestionText(e.target.value)}
            placeholder="Enter custom question"
            maxLength={500}
            style={{ flex: 1 }}
          />
          <button
            onClick={handleAddCustomQuestion}
            disabled={customRegistrationQuestions.length >= 10 || newQuestionText.length == 0}
          >
            Add
          </button>
        </div>

        {customRegistrationQuestions.map((q) => {
          const boxStyles: React.CSSProperties = {
            backgroundColor: 'white',
            borderColor: q.is_required ? 'blue' : 'transparent',
            borderWidth: q.is_required ? '2px' : '1px',
            borderStyle: 'solid',
            borderRadius: '5px',
            padding: '10px',
            maxWidth: '400px',
            marginBottom: '10px',
            transition: 'all 0.3s ease',
          };

          return (
            <div key={q.custom_registration_question_id} style={boxStyles} className='card'>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h4 style={{ flex: 1 }}>{q.custom_question_text}</h4>
                <button
                  onClick={() => handleRemoveCustomQuestion(q.custom_registration_question_id)}
                  style={{ marginLeft: '10px' }}
                  className='warningButton'
                >
                  Delete
                </button>
              </div>

              <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'right' }}>
                <label style={{ marginLeft: '10px' }}>
                  <input
                    type="radio"
                    name={`custom-${q.custom_registration_question_id}`}
                    value="true"
                    checked={q.is_required === true}
                    onChange={() => handleCustomRequiredChange(q.custom_registration_question_id, true)}
                  />
                  Required
                </label>
                <label style={{ marginLeft: '10px' }}>
                  <input
                    type="radio"
                    name={`custom-${q.custom_registration_question_id}`}
                    value="false"
                    checked={q.is_required === false}
                    onChange={() => handleCustomRequiredChange(q.custom_registration_question_id, false)}
                  />
                  Optional
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <h2>Preset Questions</h2>

      {Object.keys(groupedByCategory).map((category) => {
        const isExpanded = expandedCategories[category] ?? false; // default collapsed
        return (
          <div key={category} style={{ marginBottom: '20px' }}>
            {/* Category Header with Toggle Button */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => handleToggleCategory(category)}>
              <h3>{category} Questions:</h3>
              <button aria-label='Expand and Collapse Category Button'
                style={{ color: 'black', background: 'none', outline: 'none', boxShadow: 'none', padding: '0px' }}>
                {!isExpanded && <span>&#9654;</span>}
                {!!isExpanded && <span>&#9660;</span>}
              </button>
            </div>

            {/* Only render questions if expanded */}
            {isExpanded && (
              <div style={{ marginTop: '10px' }}>
                {groupedByCategory[category].map((question) => {
                  const selectedOption = question.option || 'Optional';

                  let boxStyles: React.CSSProperties = {
                    backgroundColor: 'white',
                    borderColor: 'transparent',
                    borderWidth: '1px',
                    borderRadius: '5px',
                    padding: '10px',
                    maxWidth: '400px',
                    marginBottom: '2px',
                    transition: 'all 0.3s ease',
                  };

                  if (selectedOption === 'Required') {
                    boxStyles = { ...boxStyles, borderColor: 'blue', borderWidth: '2px' };
                  } else if (selectedOption === 'Omit') {
                    boxStyles = { ...boxStyles, backgroundColor: '#d2d2d2' };
                  }

                  return (
                    <div key={question.preset_registration_question_id} style={boxStyles} className='card'>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <h4 style={{ flex: 1 }}>{question.question_text}</h4>
                        <button onClick={() => handleInfoClick(question.preset_registration_question_id)}>Info</button>
                      </div>
                      {selectedQuestionId === question.preset_registration_question_id && (
                        <div style={{
                          backgroundColor: '#f0f0f0',
                          padding: '10px',
                          marginTop: '10px',
                          borderRadius: '5px',
                          border: '1px solid #ccc'
                        }}>
                          <strong>Description:</strong> {question.description || 'No description available.'}
                        </div>
                      )}
                      <div style={{ marginTop: '0px', display: 'flex', justifyContent: 'right' }}>
                        {['Required', 'Optional', 'Omit'].map((option) => (
                          <label key={option} style={{ marginLeft: '10px' }}>
                            <input
                              type="radio"
                              name={`question-${question.preset_registration_question_id}`}
                              value={option}
                              checked={selectedOption === option}
                              onChange={() => handleQuestionOptionChange(question.preset_registration_question_id, option as any)}
                            />
                            {option}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      
    </div>
  );
};
